import { useEffect, useState } from "react";
import { AlertCircle, TrendingUp, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import PeriodBadge from "./PeriodBadge";

function stripPrefix(name) {
  return (name || "").replace(/^\s*#Printed\s*Tee\s*[-–:]\s*/i, "").trim();
}

function monthLabel(ym) {
  if (!ym) return "";
  try {
    const [y, m] = ym.split("-").map(Number);
    const monthName = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short" });
    return `${monthName} '${String(y).slice(-2)}`;
  } catch { return ym; }
}

export default function RetentionAlerts() {
  const [data, setData] = useState({ tier_a: [], tier_b: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/products/retention-alerts")
      .then((r) => { if (!cancel) setData(r.data || { tier_a: [], tier_b: [] }); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const tierA = data.tier_a || [];
  const tierB = data.tier_b || [];
  const total = tierA.length + tierB.length;
  if (loading && total === 0) return null;
  if (!loading && total === 0) return null;

  return (
    <div
      className="border-2 border-emerald-800/60 bg-emerald-50/40 p-4 md:p-5"
      data-testid="retention-alerts"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left"
        data-testid="retention-alerts-toggle"
      >
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-emerald-800" />
          <div>
            <div className="bm-eyebrow text-emerald-800/80">Table 11 · Restock signals</div>
            <div className="bm-headline text-xl">Retention alerts.</div>
            <PeriodBadge label="Recent weeks vs each design's own history" note="not tied to the date picker" className="mt-1" />
          </div>
          <span className="ml-2 bm-eyebrow font-mono-bm text-emerald-800 bg-emerald-100 border border-emerald-800/40 px-2 py-0.5">
            {total}
          </span>
        </div>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
        <p className="bm-eyebrow text-emerald-900/70 mt-3 max-w-[680px] leading-relaxed" data-testid="retention-definition">
          <strong>Retention</strong> = how much of a design's launch demand is still selling by its third month — month-3 units ÷ launch-window baseline. 60%+ means it has real legs beyond the initial drop, not a one-hit.
        </p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="bm-eyebrow text-emerald-800/80 mb-2">Proven legs · retention ≥ 60%</div>
            {tierA.length === 0 && (
              <div className="text-sm text-neutral-500">No qualifying designs yet.</div>
            )}
            <div className="space-y-1">
              {tierA.map((t, i) => (
                <div
                  key={t.name}
                  className="flex items-baseline gap-3 border-b border-emerald-800/15 py-1.5"
                  data-testid={`retention-tier-a-${i}`}
                >
                  <span className="font-mono-bm text-emerald-800 text-sm font-semibold tabular-nums w-14 shrink-0">
                    {Math.round((t.retention_ratio || 0) * 100)}%
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" title={t.name}>
                      {stripPrefix(t.name)}
                    </div>
                    <div className="bm-eyebrow text-neutral-500">
                      Drop {monthLabel(t.cohort_month)} · M3 {t.month_3_units}/launch {t.launch_baseline} · {t.total_units}u lifetime
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bm-eyebrow text-emerald-800/80 mb-2 inline-flex items-center gap-1">
              <TrendingUp size={11} /> Early hits · last 4w velocity 2×+
            </div>
            {tierB.length === 0 && (
              <div className="text-sm text-neutral-500">Nothing spiking hard yet.</div>
            )}
            <div className="space-y-1">
              {tierB.map((t, i) => (
                <div
                  key={t.name}
                  className="flex items-baseline gap-3 border-b border-emerald-800/15 py-1.5"
                  data-testid={`retention-tier-b-${i}`}
                >
                  <span className="font-mono-bm text-emerald-800 text-sm font-semibold tabular-nums w-14 shrink-0">
                    {t.growth_multiple}×
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" title={t.name}>
                      {stripPrefix(t.name)}
                    </div>
                    <div className="bm-eyebrow text-neutral-500">
                      Drop {monthLabel(t.cohort_month)} · last 4w {t.recent_units}u vs prior {t.prior_units}u
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
